import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NbColorHelper, NbDialogRef, NbThemeService, NbToastrService, NbCardModule, NbIconModule, NbButtonModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { Campaign } from 'src/app/models/campaign';
import { StatisticsModel } from 'src/app/models/statistics';
import { toYYYYMMDDInt } from 'src/app/shared/utils/date-formatter';
import { CampaignState, SaveCampaignDeliveries } from '../../redux';
import { CampaignService } from '../../services/campaign.service';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgChartsModule } from 'ng2-charts';

@Component({
    selector: 'app-view-stats',
    templateUrl: './stats-campaign.component.html',
    styleUrls: ['./stats-campaign.component.css'],
    imports: [NbCardModule, NbIconModule, NbButtonModule, NgIf, NgSelectComponent, FormsModule, NgFor, NgOptionComponent, NgChartsModule, DatePipe]
})
export class StatsCampaignComponent extends StatisticsModel implements OnInit, OnDestroy {
  public delivery$: Observable<any[]> = inject(Store).select(CampaignState.deliveries);
  private unsubscribe: Subject<void> = new Subject();

  public campaign: Campaign;
  public count = 0;
  public data: any;
  public options: any;
  public themeSubscription: any;
  public chartData = {
    data: null,
    dates: [],
    count: []
  };
  public timeFrame = [
    { name: $localize`1 Week`, value: 7 },
    { name: $localize`2 Weeks`, value: 14 },
    { name: $localize`1 Month`, value: 30 }
  ];
  public choosenTime = 14;

  private maxValue: any;

  constructor(
    public utilityService: UtilityService,
    private store: Store,
    private service: CampaignService,
    private toastrService: NbToastrService,
    protected ref: NbDialogRef<StatsCampaignComponent>,
    private theme: NbThemeService
  ) {
    super();
  }

  public async ngOnInit() {
    this.fetchDeliveries();

    this.delivery$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data.length > 0) {
        const found = data.find((element) => element.campaign_id === this.campaign.id);

        if (found && found.data.length > 0) {
          this.chartData.data = found.data;
          this.prepareData();
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**************************************************************/
  public async fetchDeliveries() {
    const msPerDay = 24 * 60 * 60 * 1000;
    const now = new Date();
    const ago = new Date(now.getTime() - this.choosenTime * msPerDay);
    const dateStart = toYYYYMMDDInt(ago);
    const dateEnd = toYYYYMMDDInt(now);

    const data = await this.service.getCampaignDeliveries(this.campaign.id, dateStart, dateEnd);

    this.toastrService.show(status, $localize`Fetch: Complete`, { status: 'success' });

    if (data) {
      const object = {
        data,
        campaign_id: this.campaign.id
      };
      this.store.dispatch(new SaveCampaignDeliveries(object));
    }
  }

  public close(): void {
    this.ref.close();
  }

  /**************************************************************/
  public getAverageExecDuration(): string | boolean {
    const average = super.getAverageExecDuration(this.campaign.total_duration, this.campaign.executions);
    if (typeof average === 'string') {
      return average.replace(/\(\d* seconds\)/, '');
    } else {
      return 'N/A';
    }
  }

  /**************************************************************/
  public prepareData() {
    this.chartData.dates = [];
    this.chartData.count = [];
    this.count = 0;

    this.maxValue = Math.round(Math.max(...this.chartData.data.map((element) => element.count)) / 100) * 10;

    this.chartData.data.forEach((element) => {
      const formatedDate = element.delivery_date.toString().replace(/(\d{4})(\d{2})(\d{2})/g, '$2-$3');
      this.chartData.dates.push(formatedDate);
      this.chartData.count.push(element.count);
      this.count = this.count + element.count;
    });

    this.createCharts();
  }

  public createCharts() {
    this.themeSubscription = this.theme
      .getJsTheme()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((config) => {
        const colors: any = config.variables;
        const chartjs: any = config.variables.chartjs;

        this.data = {
          labels: this.chartData.dates,
          datasets: [
            {
              data: this.chartData.count,
              label: $localize`Number of deliveries`,
              backgroundColor: NbColorHelper.hexToRgbA(colors.primaryLight, 0.7),
              borderColor: NbColorHelper.hexToRgbA(colors.primaryLight, 1),
              fill: true
            }
          ]
        };

        this.options = {
          maintainAspectRatio: true,
          responsive: true,
          scales: {
            y: {
              grid: {
                display: false
              }
            },
            x: {
              grid: {
                display: false
              }
            }
          }
        };
      });
  }
}
